/**
 * Static data for the Space Activation onboarding checklist: the steps, their
 * ordering, and the sub-steps that make them up. This is the FE's single source
 * of truth for checklist content — copy, time estimates, and how each sub-step
 * gets completed (its `trigger`). All ids are referenced via the typed
 * `ACTIVATION_STEP`/`ACTIVATION_SUB_STEP` constants so this data can't drift
 * from the parser or the pure derivations in `@lib/spaceActivation`.
 */

import { ACTIVATION_STEP, ACTIVATION_SUB_STEP, type ActivationSubStepId } from './ids';
import type { ActivationStep, ActivationSubStep } from './types';

/** The single owner-facing intro video (`setup.watch_video`), hosted on R2. Also linked from the Activation Kit. */
export const ACTIVATION_INTRO_VIDEO_SRC =
  'https://media.meetball.fun/videos/activation/intro-v1-web.mp4';

/** Every checklist sub-step, in no particular order (ordering lives on steps). */
export const ACTIVATION_SUB_STEPS: ActivationSubStep[] = [
  {
    id: ACTIVATION_SUB_STEP.SETUP_WATCH_VIDEO,
    stepId: ACTIVATION_STEP.SETUP,
    title: 'Watch short intro video',
    description: 'Learn how communities use Meetball to help people connect and participate.',
    estMinutes: '2',
    trigger: { kind: 'acknowledge' },
  },
  {
    id: ACTIVATION_SUB_STEP.SETUP_COMPLETE_SETUP,
    stepId: ACTIVATION_STEP.SETUP,
    title: 'Complete your Space setup',
    description: 'Add the details members need to understand what this Space is for.',
    estMinutes: '2',
    trigger: { kind: 'action', sites: ['space-settings'] },
  },
  {
    id: ACTIVATION_SUB_STEP.SETUP_SEED_POSTS,
    stepId: ACTIVATION_STEP.SETUP,
    title: 'Seed your Space with your first posts',
    description: 'Add 2 or 3 examples so members know what they can ask, offer or share.',
    estMinutes: '2-3',
    trigger: { kind: 'action', sites: ['create-question'], requiredCount: 2 },
  },
  {
    id: ACTIVATION_SUB_STEP.ANNOUNCE_SHARE_LINK,
    stepId: ACTIVATION_STEP.ANNOUNCE,
    title: 'Add your Space link everywhere',
    description: 'Put your Space link where your community already is.',
    estMinutes: '2',
    trigger: { kind: 'acknowledge' },
  },
  {
    id: ACTIVATION_SUB_STEP.ANNOUNCE_ANNOUNCE,
    stepId: ACTIVATION_STEP.ANNOUNCE,
    title: 'Announce your Space',
    description: "Your community won't use the space if they don't know it exists.",
    estMinutes: '2',
    trigger: { kind: 'action', sites: ['announce-drawer'], requiredCount: 2 },
  },
  {
    id: ACTIVATION_SUB_STEP.ANNOUNCE_INVITE_MEMBERS,
    stepId: ACTIVATION_STEP.ANNOUNCE,
    title: 'Invite your first members',
    description: 'When early members participate, others are much more likely to join in too.',
    estMinutes: '2',
    trigger: { kind: 'action', sites: ['invite-page'] },
  },
  {
    id: ACTIVATION_SUB_STEP.LAUNCH_LIST_EVENT,
    stepId: ACTIVATION_STEP.LAUNCH,
    title: 'List your next event on Meetball',
    description: "Let members see what's happening and who's attending.",
    estMinutes: '2',
    // Completed by EITHER path the list_event drawer offers: creating an event (create-event)
    // or connecting a Luma calendar on Setup step 4 (space-settings). Informational.
    trigger: { kind: 'action', sites: ['create-event', 'space-settings'] },
  },
  {
    id: ACTIVATION_SUB_STEP.LAUNCH_ADD_MEETBALL_APP,
    stepId: ACTIVATION_STEP.LAUNCH,
    title: 'Add Meetball to your event app',
    description: 'Bring Meetball into the tools your event already uses.',
    estMinutes: '2-3',
    trigger: { kind: 'acknowledge' },
  },
  {
    id: ACTIVATION_SUB_STEP.LAUNCH_ACTIVATION_KIT,
    stepId: ACTIVATION_STEP.LAUNCH,
    title: 'Get your activation kit',
    description:
      'QR posters and assets help attendees join Meetball and start real-life conversations.',
    estMinutes: '2',
    trigger: { kind: 'action', sites: ['activation-kit-drawer'] },
  },
];

/** Lookup of every sub-step by its id. */
export const SUB_STEPS_BY_ID = Object.fromEntries(
  ACTIVATION_SUB_STEPS.map(s => [s.id, s]),
) as Record<ActivationSubStepId, ActivationSubStep>;

/**
 * The four checklist steps in display order. `prepare_event` has no sub-steps
 * yet — an empty `subStepIds` is valid and the derivations treat such a step
 * as never complete until designed.
 */
export const ACTIVATION_STEPS: ActivationStep[] = [
  {
    id: ACTIVATION_STEP.SETUP,
    order: 1,
    title: 'Set up your Space',
    subStepIds: [
      ACTIVATION_SUB_STEP.SETUP_WATCH_VIDEO,
      ACTIVATION_SUB_STEP.SETUP_COMPLETE_SETUP,
      ACTIVATION_SUB_STEP.SETUP_SEED_POSTS,
    ],
  },
  {
    id: ACTIVATION_STEP.ANNOUNCE,
    order: 2,
    title: 'Announce your Space',
    subStepIds: [
      ACTIVATION_SUB_STEP.ANNOUNCE_SHARE_LINK,
      ACTIVATION_SUB_STEP.ANNOUNCE_ANNOUNCE,
      ACTIVATION_SUB_STEP.ANNOUNCE_INVITE_MEMBERS,
    ],
  },
  {
    id: ACTIVATION_STEP.LAUNCH,
    order: 3,
    title: 'Launch your Space',
    subStepIds: [
      ACTIVATION_SUB_STEP.LAUNCH_LIST_EVENT,
      ACTIVATION_SUB_STEP.LAUNCH_ADD_MEETBALL_APP,
      ACTIVATION_SUB_STEP.LAUNCH_ACTIVATION_KIT,
    ],
  },
  {
    id: ACTIVATION_STEP.PREPARE_EVENT,
    order: 4,
    title: 'Prepare your event',
    subStepIds: [],
  },
];
